import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HeartIcon, ShieldCheckIcon, TruckIcon, StarIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon, StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { useProduct, useProductReviews, useAddReview, useAddToWishlist, useRemoveFromWishlist, useWishlist } from '../hooks/useProducts';
import { useAuthStore } from '../stores/authStore';
import { useCurrency } from '../hooks/useCurrency';

export default function ProductPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuthStore();

  const { data: product, isLoading, error } = useProduct(slug || '');
  const { data: reviews } = useProductReviews(product?.id || '');
  const { data: wishlist } = useWishlist();

  const addToWishlist = useAddToWishlist();
  const removeFromWishlist = useRemoveFromWishlist();
  const addReview = useAddReview(product?.id || '');
  const { format: formatPrice } = useCurrency();

  const [selectedImage, setSelectedImage] = useState(0);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);

  const isInWishlist = wishlist?.some((item) => item.id === product?.id) || false;

  const handleWishlistClick = () => {
    if (!product) return;
    if (isInWishlist) {
      removeFromWishlist.mutate(product.id);
    } else {
      addToWishlist.mutate(product.id);
    }
  };

  const handleReviewSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (reviewRating === 0) return;

    try {
      await addReview.mutateAsync({ rating: reviewRating, comment: reviewComment });
      setReviewSubmitted(true);
      setShowReviewForm(false);
      setReviewRating(0);
      setReviewComment('');
    } catch {
      // Error handling - the error will show if user already reviewed
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="aspect-square bg-gray-200 animate-pulse rounded-lg" />
          <div className="space-y-4">
            <div className="h-8 bg-gray-200 animate-pulse rounded w-3/4" />
            <div className="h-6 bg-gray-200 animate-pulse rounded w-1/2" />
            <div className="h-10 bg-gray-200 animate-pulse rounded w-1/3" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="container mx-auto px-4 py-6 text-center">
        <h1 className="text-2xl font-bold text-gray-900">{t('common.error')}</h1>
        <p className="text-gray-500 mt-2">{t('product.noProducts')}</p>
        <Link to="/" className="btn btn-primary mt-4">
          {t('common.back')}
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Image Gallery */}
        <div className="space-y-4">
          {/* Main Image */}
          <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
            <img
              src={product.images[selectedImage] || '/placeholder-product.jpg'}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Thumbnails */}
          {product.images.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-2">
              {product.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                    selectedImage === index ? 'border-primary' : 'border-gray-200'
                  }`}
                >
                  <img src={image} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="space-y-6">
          {/* Category Breadcrumb */}
          {product.category && (
            <nav className="text-sm text-gray-500">
              <Link to="/" className="hover:text-primary">{t('nav.home')}</Link>
              <span className="mx-2">/</span>
              <Link to={`/category/${product.category.slug}`} className="hover:text-primary">
                {product.category.name}
              </Link>
            </nav>
          )}

          {/* Brand */}
          {product.brand && (
            <span className="inline-block text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-100 px-2 py-1 rounded">
              {product.brand.replace(/_/g, ' ')}
            </span>
          )}

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900">{product.name}</h1>

          {/* Supplier Badge */}
          {product.supplier && (
            <div className="flex items-center gap-2 p-3 bg-teal-50 rounded-lg">
              <CheckBadgeIcon className="w-6 h-6 text-teal-600" />
              <div>
                <p className="font-medium text-teal-800">
                  {t('product.fromSupplier')} {product.supplier.name}
                </p>
                {product.supplier.isVerified && (
                  <p className="text-sm text-teal-600">{t('product.verifiedSupplier')}</p>
                )}
              </div>
            </div>
          )}

          {/* Rating */}
          {product.rating !== undefined && product.rating > 0 && (
            <div className="flex items-center gap-2">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  star <= Math.round(product.rating!)
                    ? <StarSolidIcon key={star} className="w-5 h-5 text-yellow-400" />
                    : <StarIcon key={star} className="w-5 h-5 text-gray-300" />
                ))}
              </div>
              <span className="text-sm text-gray-600">
                ({product.reviewCount || 0} {t('reviews.title')})
              </span>
            </div>
          )}

          {/* Price */}
          <div className="text-3xl font-bold text-primary">
            {formatPrice(product.price)}
          </div>

          {/* Trust Badges */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <ShieldCheckIcon className="w-5 h-5 text-green-600" />
              <span className="text-sm">{t('product.securePayment')}</span>
            </div>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <TruckIcon className="w-5 h-5 text-blue-600" />
              <span className="text-sm">{t('product.fastShipping')}</span>
            </div>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <CheckBadgeIcon className="w-5 h-5 text-purple-600" />
              <span className="text-sm">{t('product.trustedSupplier')}</span>
            </div>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <StarIcon className="w-5 h-5 text-yellow-500" />
              <span className="text-sm">{t('product.excellentQuality')}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4">
            {isAuthenticated ? (
              product.supplierUrl ? (
                <a
                  href={product.supplierUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 btn btn-primary py-3 text-center"
                >
                  {t('product.buy')}
                </a>
              ) : (
                <button
                  onClick={() => alert(t('product.notAvailable'))}
                  className="flex-1 btn btn-primary py-3 text-center"
                >
                  {t('product.buy')}
                </button>
              )
            ) : (
              <Link
                to="/login"
                className="flex-1 btn btn-primary py-3 text-center"
              >
                {t('auth.login')}
              </Link>
            )}
            {isAuthenticated && (
              <button
                onClick={handleWishlistClick}
                className="p-3 border border-gray-300 rounded-lg hover:border-red-500 transition-colors"
                aria-label={isInWishlist ? t('wishlist.remove') : t('wishlist.add')}
              >
                {isInWishlist ? (
                  <HeartSolidIcon className="w-6 h-6 text-red-500" />
                ) : (
                  <HeartIcon className="w-6 h-6 text-gray-600" />
                )}
              </button>
            )}
          </div>

          {/* Description */}
          {product.description && (
            <div className="prose prose-sm max-w-none">
              <h3 className="text-lg font-semibold">{t('product.categories')}</h3>
              <p className="text-gray-600">{product.description}</p>
            </div>
          )}

          {/* Warnings */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-semibold text-yellow-800 mb-2">
              {t('product.readBeforeOrder')}
            </h3>
            <ul className="text-sm text-yellow-700 space-y-2">
              <li>• {t('product.warning1')}</li>
              <li>• {t('product.warning2')}</li>
              <li>• {t('product.sizeChart')}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Reviews Section */}
      <section className="mt-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">{t('reviews.title')}</h2>
          {isAuthenticated && !showReviewForm && !reviewSubmitted && (
            <button
              onClick={() => setShowReviewForm(true)}
              className="btn btn-primary text-sm"
            >
              {t('reviews.writeReview')}
            </button>
          )}
        </div>

        {/* Success message after submission */}
        {reviewSubmitted && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
            <p className="font-medium">{t('reviews.submitted')}</p>
            <p className="text-sm">{t('reviews.pendingApproval')}</p>
          </div>
        )}

        {/* Review Form */}
        {showReviewForm && (
          <form onSubmit={handleReviewSubmit} className="bg-white p-6 rounded-lg shadow-sm mb-6 border border-gray-200">
            <h3 className="font-semibold mb-4">{t('reviews.writeReview')}</h3>

            {/* Star Rating */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('reviews.yourRating')} *
              </label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setReviewRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    {star <= (hoverRating || reviewRating) ? (
                      <StarSolidIcon className="w-8 h-8 text-yellow-400" />
                    ) : (
                      <StarIcon className="w-8 h-8 text-gray-300" />
                    )}
                  </button>
                ))}
              </div>
              {reviewRating === 0 && (
                <p className="text-sm text-gray-500 mt-1">{t('reviews.selectRating')}</p>
              )}
            </div>

            {/* Comment */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('reviews.yourReview')}
              </label>
              <textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder={t('reviews.reviewPlaceholder')}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors resize-none"
                rows={4}
              />
            </div>

            {/* Error message */}
            {addReview.error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
                {t('reviews.alreadyReviewed')}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={reviewRating === 0 || addReview.isPending}
                className="btn btn-primary disabled:opacity-50"
              >
                {addReview.isPending ? t('common.loading') : t('reviews.submit')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowReviewForm(false);
                  setReviewRating(0);
                  setReviewComment('');
                }}
                className="btn btn-secondary"
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
        )}

        {/* Login prompt for non-authenticated users */}
        {!isAuthenticated && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-blue-700">
              {t('reviews.loginToReview')}{' '}
              <Link to="/login" className="font-medium underline hover:text-blue-800">
                {t('auth.login')}
              </Link>
            </p>
          </div>
        )}

        {/* Reviews List */}
        {reviews && reviews.length > 0 ? (
          <div className="space-y-4">
            {reviews.map((review) => (
              <div key={review.id} className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      star <= review.rating
                        ? <StarSolidIcon key={star} className="w-4 h-4 text-yellow-400" />
                        : <StarIcon key={star} className="w-4 h-4 text-gray-300" />
                    ))}
                  </div>
                  <span className="font-medium">{review.userName}</span>
                  <span className="text-sm text-gray-500">
                    {new Date(review.createdAt).toLocaleDateString()}
                  </span>
                </div>
                {review.comment && (
                  <p className="text-gray-600">{review.comment}</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <p className="text-gray-500">{t('reviews.noReviews')}</p>
            <p className="text-sm text-gray-400 mt-1">{t('reviews.beFirst')}</p>
          </div>
        )}
      </section>
    </div>
  );
}
